import {Component} from 'react';
import {CrudStatus, EntryStatus, CrudStep} from '../types';
import {Dictionary} from 'lodash';
import omit from 'lodash/omit';
import scrollToBottom from './scrollToBottom';

interface State<T extends object> {
    creates: Dictionary<T>;
    deletes: Dictionary<T>;
    entries: T[];
    status: CrudStatus;
    statuses: Array<EntryStatus<T>>;
    step: CrudStep;
    updates: Dictionary<T>;
}

type SetStateFn<T extends object> = Component<void, State<T>>['setState'];

export default async <T extends object>(
    setState: SetStateFn<T>,
    idName: string,
    createFn: (entry: T) => Promise<void>,
    updateFn: (entry: T) => Promise<void>,
    deleteFn: (entry: T) => Promise<void>,
    getErrorResponseMessage: (error: Error) => string
) => {
    setState({ status: 'saving', statuses: [], step: 'save' });

    const getState = () => new Promise<State<T>>((r) => setState(r));

    const state = await getState();

    const ops = [
        ...Object.values(state.creates).map((entry) => ({
            entry,
            fn: createFn,
            key: 'creates' as const,
            msgBegin: 'Creating...',
            msgEnd: 'Created',
        })),
        ...Object.values(state.updates).map((entry) => ({
            entry,
            fn: updateFn,
            key: 'updates' as const,
            msgBegin: 'Updating...',
            msgEnd: 'Updated',
        })),
        ...Object.values(state.deletes).map((entry) => ({
            entry,
            fn: deleteFn,
            key: 'deletes' as const,
            msgBegin: 'Deleting...',
            msgEnd: 'Deleted',
        })),
    ];

    let success = true;
    for (const [i, {entry, fn, msgBegin, msgEnd, key}] of Object.entries(ops)) {
        if ((await getState()).status === 'interrupted') {
            break;
        }
        setState((s) => ({ statuses: [
            ...s.statuses,
            { entry, message: msgBegin, status: 'saving' },
        ]}));
        setTimeout(scrollToBottom, 100);
        try {
            await fn(entry);
            setState((s) => ({
                statuses: s.statuses.map((st, j) => (+i !== +j) ? st : {
                    entry,
                    message: msgEnd,
                    status: 'saved',
                }),
                [key]: omit(s[key], entry[idName]),
            } as unknown as State<T>));
        } catch (e) {
            setState(({ statuses }) => ({
                statuses: statuses.map((st, j) => (+i !== j) ? st : {
                    entry,
                    message: getErrorResponseMessage(e),
                    status: 'error',
                }),
            }));
            success = false;
        }
    }

    setState({ status: success ? 'saved' : 'error' });
};
